import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/Button";
import type { JobDetail } from "../types";

interface Props {
  job: JobDetail;
  onAnalyze: () => Promise<void>;
  analyzing: boolean;
}

export default function AnalysisPanel({ job, onAnalyze, analyzing }: Props) {
  const [showAnalysis, setShowAnalysis] = useState(false);

  const hasScores = job.alignment_score !== null && job.success_prob !== null;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 m-0">
          Match Analysis
        </h2>
        <Button
          onClick={onAnalyze}
          disabled={analyzing}
          loading={analyzing}
          variant="primary"
          size="sm"
        >
          {analyzing ? "Analysing…" : hasScores ? "Re-analyse" : "Analyse Match"}
        </Button>
      </div>

      {hasScores ? (
        <>
          <div className="flex gap-6 mb-4">
            <ScoreCard label="Alignment Score" value={job.alignment_score!} colorClass="text-accent" />
            <ScoreCard label="Success Probability" value={job.success_prob!} colorClass="text-success" />
          </div>
          {job.match_analysis && (
            <div>
              <button
                onClick={() => setShowAnalysis((v) => !v)}
                className="text-sm text-accent underline bg-transparent border-none cursor-pointer p-0 hover:text-accent-hover transition-colors duration-200"
              >
                {showAnalysis ? "Hide" : "Show"} detailed analysis
              </button>
              {showAnalysis && (
                <div className="mt-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-md overflow-visible pdf-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{job.match_analysis}</ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="text-slate-400 m-0 text-sm">
          Run analysis to see how well your profile matches this job.
        </p>
      )}
    </div>
  );
}

function ScoreCard({ label, value, colorClass }: { label: string; value: number; colorClass: string }) {
  return (
    <div className="flex-1 bg-slate-50 dark:bg-slate-800 rounded-card px-5 py-4 text-center">
      <div className={`text-4xl font-bold ${colorClass}`}>{value}%</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</div>
    </div>
  );
}
